import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Pickaxe, 
  Wallet, 
  TrendingUp, 
  TrendingDown,
  Calculator,
  Gift,
  Settings,
  LogOut,
  Plus,
  Trash2,
  RefreshCw,
  Shield,
  AlertCircle,
  CheckCircle,
  DollarSign,
  Activity,
  Coins,
  BarChart3,
  Eye,
  EyeOff,
  QrCode,
  Copy,
  ExternalLink,
  Loader2,
  Cpu,
  Bell,
  Search,
  Zap,
  ArrowRightLeft,
  Sparkles,
  X,
  HelpCircle,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  Clock
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Toaster, toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lightweight client-side obfuscation for Binance keys in sessionStorage.
// Not cryptographically strong — real protection comes from keys being read-only
// and never leaving the user's browser except per-request to our backend proxy.
const BKEY_STORE = 'binanceKeys_v1';
const stashBinanceKeys = (apiKey, apiSecret) => {
  sessionStorage.setItem(BKEY_STORE, btoa(JSON.stringify({ apiKey, apiSecret })));
};
const loadBinanceKeys = () => {
  try {
    const raw = sessionStorage.getItem(BKEY_STORE);
    if (!raw) return null;
    return JSON.parse(atob(raw));
  } catch { return null; }
};
const clearBinanceKeys = () => sessionStorage.removeItem(BKEY_STORE);

const tabs = [
  { id: 'guide', label: 'Getting Started', icon: HelpCircle },
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'pools', label: 'Pool Monitor', icon: Activity },
  { id: 'miners', label: 'Miners', icon: Cpu },
  { id: 'calculator', label: 'Profitability', icon: Calculator },
  { id: 'customcoins', label: 'Custom Coins', icon: Sparkles },
  { id: 'binance', label: 'Binance', icon: Coins },
  { id: 'opportunities', label: 'Opportunities', icon: Gift },
  { id: 'arbitrage', label: 'Arbitrage', icon: ArrowRightLeft },
  { id: 'wallet', label: 'Wallet Scan', icon: Search },
  { id: 'alerts', label: 'Price Alerts', icon: Bell },
  { id: 'schedules', label: 'Schedules', icon: Clock },
  { id: 'settings', label: 'Settings', icon: Settings },
];

export const CryptoDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('guideSeen') ? 'dashboard' : 'guide';
  });
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [prices, setPrices] = useState({});
  const [pools, setPools] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [opportunities, setOpportunities] = useState({ airdrops: [], market_signals: [] });

  const token = localStorage.getItem('cryptoToken');

  useEffect(() => {
    if (!token) {
      navigate('/crypto');
      return;
    }
    loadData();
  }, [token, navigate]);

  const loadData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [userRes, pricesRes, poolsRes, walletsRes] = await Promise.all([
        axios.get(`${API}/crypto/me`, { headers }),
        axios.get(`${API}/crypto/prices`),
        axios.get(`${API}/crypto/pools`),
        axios.get(`${API}/crypto/wallets`, { headers })
      ]);
      
      setUser(userRes.data);
      setPrices(pricesRes.data);
      setPools(poolsRes.data);
      setWallets(walletsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('cryptoToken');
        navigate('/crypto');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('cryptoToken');
    localStorage.removeItem('cryptoUsername');
    navigate('/crypto');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <Loader2 className="animate-spin text-purple-500" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900" data-testid="crypto-dashboard">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <div className="p-2 rounded-lg bg-gradient-to-br from-purple-600 to-cyan-600">
                <Pickaxe className="text-white" size={24} />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">CryptoMiner Dashboard</h1>
                <p className="text-xs text-gray-400">Welcome, {user?.username}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <NotificationsBell token={token} />
              <button
                onClick={loadData}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                title="Refresh Data"
                data-testid="refresh-data-btn"
              >
                <RefreshCw size={18} />
              </button>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="text-gray-400 hover:text-white"
                data-testid="logout-btn"
              >
                <LogOut size={18} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className="w-56 flex-shrink-0">
            <nav className="space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    activeTab === tab.id
                      ? 'bg-purple-600/20 text-purple-400 border border-purple-500/30'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <tab.icon size={20} />
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              {activeTab === 'guide' && (
                <GuideTab setActiveTab={setActiveTab} />
              )}
              {activeTab === 'dashboard' && (
                <DashboardTab prices={prices} wallets={wallets} />
              )}
              {activeTab === 'pools' && (
                <PoolsTab pools={pools} wallets={wallets} setWallets={setWallets} token={token} />
              )}
              {activeTab === 'miners' && (
                <MinersTab token={token} />
              )}
              {activeTab === 'calculator' && (
                <CalculatorTab prices={prices} />
              )}
              {activeTab === 'customcoins' && (
                <CustomCoinsTab token={token} />
              )}
              {activeTab === 'binance' && (
                <BinanceTab token={token} />
              )}
              {activeTab === 'opportunities' && (
                <OpportunitiesTab token={token} />
              )}
              {activeTab === 'arbitrage' && (
                <ArbitrageTab token={token} />
              )}
              {activeTab === 'wallet' && (
                <WalletScanTab token={token} />
              )}
              {activeTab === 'alerts' && (
                <AlertsTab token={token} prices={prices} />
              )}
              {activeTab === 'schedules' && (
                <SchedulesTab token={token} />
              )}
              {activeTab === 'settings' && (
                <SettingsTab user={user} token={token} setUser={setUser} />
              )}
            </motion.div>
          </main>
        </div>
      </div>
    </div>
  );
};

// Dashboard Tab
const DashboardTab = ({ prices, wallets }) => {
  const topCoins = Object.values(prices).slice(0, 8);
  
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white">Market Overview</h2>
      
      {/* Price Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {topCoins.map((coin) => (
          <motion.div
            key={coin.symbol}
            whileHover={{ scale: 1.02 }}
            className="bg-gray-800 rounded-xl p-4 border border-gray-700"
          >
            <div className="flex justify-between items-start mb-2">
              <span className="text-lg font-bold text-white">{coin.symbol}</span>
              <span className={`text-sm font-medium ${coin.change_24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {coin.change_24h >= 0 ? '+' : ''}{coin.change_24h?.toFixed(2)}%
              </span>
            </div>
            <p className="text-2xl font-bold text-white">${coin.price_usd?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
            <p className="text-xs text-gray-400 mt-1">{coin.name}</p>
          </motion.div>
        ))}
      </div>

      <CompareWidget prices={prices} />

      {/* Wallets Summary */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <h3 className="text-lg font-bold text-white mb-4">Tracked Wallets ({wallets.length})</h3>
        {wallets.length === 0 ? (
          <p className="text-gray-400">No wallets tracked yet. Add wallets in the Pool Monitor tab.</p>
        ) : (
          <div className="space-y-3">
            {wallets.map((wallet) => (
              <div key={wallet.id} className="flex justify-between items-center p-3 bg-gray-900 rounded-lg">
                <div>
                  <p className="font-medium text-white">{wallet.label}</p>
                  <p className="text-xs text-gray-400 font-mono">{wallet.address.slice(0, 12)}...{wallet.address.slice(-8)}</p>
                </div>
                <span className="px-2 py-1 bg-purple-600/20 text-purple-400 rounded text-sm">{wallet.coin}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============ Compare Widget — Current Miner vs Best Alternative ============
const CompareWidget = ({ prices }) => {
  const token = localStorage.getItem('cryptoToken');
  const [config, setConfig] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('compareConfig') || '{}') || {};
    } catch { return {}; }
  });
  const hashrateUnit = { H: 1, KH: 1e3, MH: 1e6, GH: 1e9, TH: 1e12 };
  const [form, setForm] = useState({
    current_coin: config.current_coin || 'XMR',
    hashrate: config.hashrate || 1000,
    hashrateUnit: config.hashrateUnit || 'H',
    power_watts: config.power_watts || 100,
    power_cost_kwh: config.power_cost_kwh || 0.10,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const persist = (v) => {
    setForm(v);
    localStorage.setItem('compareConfig', JSON.stringify(v));
  };

  const run = async () => {
    setLoading(true);
    try {
      const hr = form.hashrate * hashrateUnit[form.hashrateUnit];
      const res = await axios.post(`${API}/crypto/mining/compare`, {
        current_coin: form.current_coin,
        hashrate: hr,
        power_watts: form.power_watts,
        power_cost_kwh: form.power_cost_kwh,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResult(res.data);
      setConfig(form);
      localStorage.setItem('compareConfig', JSON.stringify(form));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Compare failed');
    } finally {
      setLoading(false);
    }
  };

  // Auto-run once on mount if config is persisted
  useEffect(() => {
    if (config.current_coin) run();
    // eslint-disable-next-line
  }, []);

  const allCoins = Object.keys(prices);

  return (
    <div className="bg-gradient-to-br from-indigo-500/10 to-emerald-500/10 rounded-xl p-6 border border-indigo-500/30" data-testid="compare-widget">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Sparkles className="text-indigo-400" size={20} />
          Current Miner vs Best Alternative
        </h3>
        <Button size="sm" onClick={run} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="compare-run-btn">
          {loading ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
        </Button>
      </div>

      <div className="grid md:grid-cols-5 gap-2 mb-4">
        <select value={form.current_coin} onChange={e => persist({...form, current_coin: e.target.value})} className="px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm" data-testid="compare-coin-select">
          {(allCoins.length ? allCoins : ['BTC','ETC','XMR','RVN','ERG','KAS','FLUX','ZEC','LTC','DOGE']).map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="flex gap-1 md:col-span-2">
          <Input type="number" value={form.hashrate} onChange={e => persist({...form, hashrate: parseFloat(e.target.value) || 0})} className="bg-gray-900 border-gray-700 text-white text-sm flex-1" placeholder="Hashrate" data-testid="compare-hashrate-input" />
          <select value={form.hashrateUnit} onChange={e => persist({...form, hashrateUnit: e.target.value})} className="px-2 bg-gray-900 border border-gray-700 rounded text-white text-sm">
            {Object.keys(hashrateUnit).map(u => <option key={u} value={u}>{u}/s</option>)}
          </select>
        </div>
        <Input type="number" value={form.power_watts} onChange={e => persist({...form, power_watts: parseFloat(e.target.value) || 0})} className="bg-gray-900 border-gray-700 text-white text-sm" placeholder="Watts" data-testid="compare-power-input" />
        <Input type="number" step="0.01" value={form.power_cost_kwh} onChange={e => persist({...form, power_cost_kwh: parseFloat(e.target.value) || 0})} className="bg-gray-900 border-gray-700 text-white text-sm" placeholder="$/kWh" data-testid="compare-cost-input" />
      </div>

      {result && (
        <div className="space-y-3" data-testid="compare-result">
          <div className="grid grid-cols-2 gap-3">
            <div className="p-4 bg-gray-800/60 rounded-lg border border-gray-700">
              <p className="text-xs text-gray-400">Current ({result.current.coin})</p>
              <p className={`text-2xl font-bold ${result.current.daily_profit_usd >= 0 ? 'text-white' : 'text-red-400'}`}>
                ${result.current.daily_profit_usd.toFixed(4)}/day
              </p>
              <p className="text-xs text-gray-500 mt-1">{result.current.algorithm} · {result.current.live_data ? 'live' : 'static'}</p>
            </div>
            <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
              <p className="text-xs text-emerald-400">Best Alternative ({result.best_alternative.coin})</p>
              <p className="text-2xl font-bold text-emerald-400">${result.best_alternative.daily_profit_usd.toFixed(4)}/day</p>
              <p className="text-xs text-gray-500 mt-1">{result.best_alternative.algorithm} · {result.best_alternative.live_data ? 'live' : 'static'}</p>
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${result.recommendation === 'SWITCH' ? 'bg-amber-500/10 border-amber-500/30' : 'bg-gray-800/60 border-gray-700'}`} data-testid="compare-recommendation">
            <div className="flex items-center gap-3">
              <span className={`text-xs font-bold px-3 py-1 rounded ${result.recommendation === 'SWITCH' ? 'bg-amber-500 text-black' : 'bg-gray-700 text-gray-300'}`}>
                {result.recommendation}
              </span>
              <p className="text-sm text-white flex-1">{result.message}</p>
            </div>
            {result.recommendation === 'SWITCH' && (
              <div className="mt-2 flex gap-4 text-sm">
                <span className="text-amber-300">
                  <TrendingUp size={14} className="inline mr-1" />
                  +${result.delta_daily_usd}/day
                </span>
                <span className="text-amber-300">
                  +${result.delta_monthly_usd}/month
                </span>
                <span className="text-amber-300">
                  {result.delta_percent > 0 ? '+' : ''}{result.delta_percent}%
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      {!result && !loading && (
        <p className="text-sm text-gray-400 text-center py-4">Enter your rig specs above and click refresh to see a live comparison.</p>
      )}
    </div>
  );
};

// Pools Tab
const PoolsTab = ({ pools, wallets, setWallets, token }) => {
  const [selectedPool, setSelectedPool] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [walletLabel, setWalletLabel] = useState('');
  const [minerStats, setMinerStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const selectedPoolData = pools.find(p => p.id === selectedPool);

  const fetchMinerStats = async (poolId, address) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/crypto/miner-stats/${poolId}/${address}`);
      setMinerStats(response.data);
    } catch (err) {
      toast.error('Unable to fetch miner stats');
      setMinerStats(null);
    } finally {
      setIsLoading(false);
    }
  };

  const addWallet = async () => {
    if (!selectedPool || !walletAddress) return;
    
    try {
      const response = await axios.post(`${API}/crypto/wallets`, {
        address: walletAddress,
        coin: selectedPoolData.coin,
        pool_id: selectedPool,
        label: walletLabel || `${selectedPoolData.coin} Wallet`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setWallets(prev => [...prev, response.data]);
      setWalletAddress('');
      setWalletLabel('');
      toast.success('Wallet added successfully');
    } catch (err) {
      toast.error('Failed to add wallet');
    }
  };

  const removeWallet = async (walletId) => {
    try {
      await axios.delete(`${API}/crypto/wallets/${walletId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWallets(prev => prev.filter(w => w.id !== walletId));
      toast.success('Wallet removed');
    } catch (err) {
      toast.error('Failed to remove wallet');
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white">Pool Monitor</h2>
      
      {/* Add Wallet Form */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <h3 className="text-lg font-bold text-white mb-4">Track New Wallet</h3>
        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">Mining Pool</label>
            <select
              value={selectedPool}
              onChange={(e) => setSelectedPool(e.target.value)}
              className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
            >
              <option value="">Select pool...</option>
              {pools.map((pool) => (
                <option key={pool.id} value={pool.id}>{pool.name} ({pool.coin})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-2">Wallet Address</label>
            <Input
              value={walletAddress}
              onChange={(e) => setWalletAddress(e.target.value)}
              placeholder="0x..."
              className="bg-gray-900 border-gray-700 text-white"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-2">Label (optional)</label>
            <Input
              value={walletLabel}
              onChange={(e) => setWalletLabel(e.target.value)}
              placeholder="My Mining Rig"
              className="bg-gray-900 border-gray-700 text-white"
            />
          </div>
          <div className="flex items-end gap-2">
            <Button
              onClick={() => fetchMinerStats(selectedPool, walletAddress)}
              disabled={!selectedPool || !walletAddress || isLoading}
              className="bg-gray-700 hover:bg-gray-600 text-white"
            >
              {isLoading ? <Loader2 className="animate-spin" size={18} /> : 'Check'}
            </Button>
            <Button
              onClick={addWallet}
              disabled={!selectedPool || !walletAddress}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Plus size={18} className="mr-1" /> Add
            </Button>
          </div>
        </div>

        {/* Miner Stats Preview */}
        {minerStats && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-4 bg-gray-900 rounded-lg border border-purple-500/30"
          >
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-xs text-gray-400">Current Hashrate</p>
                <p className="text-lg font-bold text-green-400">{(minerStats.current_hashrate / 1e6).toFixed(2)} MH/s</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Average Hashrate</p>
                <p className="text-lg font-bold text-white">{(minerStats.average_hashrate / 1e6).toFixed(2)} MH/s</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Unpaid Balance</p>
                <p className="text-lg font-bold text-yellow-400">{minerStats.unpaid_balance?.toFixed(6)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Workers</p>
                <p className="text-lg font-bold text-cyan-400">{minerStats.workers?.length || 0}</p>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Tracked Wallets */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <h3 className="text-lg font-bold text-white mb-4">Tracked Wallets</h3>
        {wallets.length === 0 ? (
          <p className="text-gray-400 text-center py-8">No wallets tracked yet</p>
        ) : (
          <div className="space-y-3">
            {wallets.map((wallet) => (
              <WalletCard key={wallet.id} wallet={wallet} onRemove={removeWallet} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const WalletCard = ({ wallet, onRemove }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/crypto/miner-stats/${wallet.pool_id}/${wallet.address}`);
      setStats(response.data);
    } catch (err) {
      toast.error('Unable to fetch stats');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 bg-gray-900 rounded-lg border border-gray-700">
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-white">{wallet.label}</span>
            <span className="px-2 py-0.5 bg-purple-600/20 text-purple-400 rounded text-xs">{wallet.coin}</span>
          </div>
          <p className="text-xs text-gray-400 font-mono mt-1">{wallet.address}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchStats} className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg">
            {loading ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
          </button>
          <button onClick={() => onRemove(wallet.id)} className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-800 rounded-lg">
            <Trash2 size={16} />
          </button>
        </div>
      </div>
      
      {stats && (
        <div className="grid grid-cols-4 gap-4 text-center pt-3 border-t border-gray-700">
          <div>
            <p className="text-xs text-gray-400">Hashrate</p>
            <p className="font-bold text-green-400">{(stats.current_hashrate / 1e6).toFixed(2)} MH/s</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Unpaid</p>
            <p className="font-bold text-yellow-400">{stats.unpaid_balance?.toFixed(6)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Total Paid</p>
            <p className="font-bold text-white">{stats.total_paid?.toFixed(4)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400">Workers</p>
            <p className="font-bold text-cyan-400">{stats.workers?.length || 0}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Calculator Tab
const CalculatorTab = ({ prices }) => {
  const [formData, setFormData] = useState({
    coin: 'ETC',
    hashrate: 500,
    hashrateUnit: 'MH/s',
    power: 200,
    powerCost: 0.10
  });
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const hashrateMultipliers = {
    'H/s': 1,
    'KH/s': 1e3,
    'MH/s': 1e6,
    'GH/s': 1e9,
    'TH/s': 1e12
  };

  const calculate = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/crypto/profitability`, {
        coin: formData.coin,
        hashrate: formData.hashrate * hashrateMultipliers[formData.hashrateUnit],
        power_watts: formData.power,
        power_cost_kwh: formData.powerCost
      });
      setResult(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Calculation failed');
    } finally {
      setIsLoading(false);
    }
  };

  const coins = ['ETC', 'RVN', 'ERG', 'KAS', 'XMR', 'FLUX'];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white">Profitability Calculator</h2>
      
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input Form */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-bold text-white mb-4">Mining Parameters</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Coin</label>
              <select
                value={formData.coin}
                onChange={(e) => setFormData({ ...formData, coin: e.target.value })}
                className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white"
              >
                {coins.map((coin) => (
                  <option key={coin} value={coin}>{coin} - {prices[coin]?.name || coin}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Hashrate</label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={formData.hashrate}
                  onChange={(e) => setFormData({ ...formData, hashrate: parseFloat(e.target.value) })}
                  className="flex-1 bg-gray-900 border-gray-700 text-white"
                />
                <select
                  value={formData.hashrateUnit}
                  onChange={(e) => setFormData({ ...formData, hashrateUnit: e.target.value })}
                  className="px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
                >
                  {Object.keys(hashrateMultipliers).map((unit) => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Power Consumption (Watts)</label>
              <Input
                type="number"
                value={formData.power}
                onChange={(e) => setFormData({ ...formData, power: parseFloat(e.target.value) })}
                className="bg-gray-900 border-gray-700 text-white"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Electricity Cost ($/kWh)</label>
              <Input
                type="number"
                step="0.01"
                value={formData.powerCost}
                onChange={(e) => setFormData({ ...formData, powerCost: parseFloat(e.target.value) })}
                className="bg-gray-900 border-gray-700 text-white"
              />
            </div>

            <Button
              onClick={calculate}
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 text-white py-3"
            >
              {isLoading ? <Loader2 className="animate-spin mr-2" size={18} /> : <Calculator className="mr-2" size={18} />}
              Calculate Profitability
            </Button>
          </div>
        </div>

        {/* Results */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-bold text-white mb-4">Estimated Results</h3>
          
          {result ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-900 rounded-lg">
                  <p className="text-xs text-gray-400">Daily Revenue</p>
                  <p className="text-xl font-bold text-green-400">${result.daily_revenue_usd.toFixed(4)}</p>
                </div>
                <div className="p-4 bg-gray-900 rounded-lg">
                  <p className="text-xs text-gray-400">Daily Power Cost</p>
                  <p className="text-xl font-bold text-red-400">-${result.daily_power_cost.toFixed(4)}</p>
                </div>
              </div>
              
              <div className="p-4 bg-gradient-to-r from-purple-900/50 to-cyan-900/50 rounded-lg border border-purple-500/30">
                <p className="text-sm text-gray-400">Daily Profit</p>
                <p className={`text-3xl font-bold ${result.daily_profit_usd >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  ${result.daily_profit_usd.toFixed(4)}
                </p>
              </div>

              <div className="p-4 bg-gray-900 rounded-lg">
                <p className="text-sm text-gray-400">Monthly Profit (30 days)</p>
                <p className={`text-2xl font-bold ${result.monthly_profit_usd >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  ${result.monthly_profit_usd.toFixed(2)}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-gray-900 rounded-lg">
                  <p className="text-gray-400">Coin Price</p>
                  <p className="text-white font-medium">${result.coin_price.toFixed(4)}</p>
                </div>
                <div className="p-3 bg-gray-900 rounded-lg">
                  <p className="text-gray-400">Algorithm</p>
                  <p className="text-white font-medium">{result.algorithm}</p>
                </div>
                <div className="p-3 bg-gray-900 rounded-lg">
                  <p className="text-gray-400">Network Hashrate</p>
                  <p className="text-white font-medium">{result.network_hashrate}</p>
                </div>
                <div className="p-3 bg-gray-900 rounded-lg">
                  <p className="text-gray-400">Block Reward</p>
                  <p className="text-white font-medium">{result.block_reward} {result.coin}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              <Calculator size={48} className="mx-auto mb-4 opacity-50" />
              <p>Enter your mining parameters and click Calculate</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Binance Tab - CLIENT-SIDE KEYS ONLY (never stored on server)
const BinanceTab = ({ token }) => {
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [balances, setBalances] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasKeys, setHasKeys] = useState(!!loadBinanceKeys());
  const [depositAlertThreshold, setDepositAlertThreshold] = useState(
    Number(localStorage.getItem('depositAlertThreshold') || 100)
  );

  const buildHeaders = () => {
    const keys = loadBinanceKeys();
    if (!keys) return null;
    return {
      Authorization: `Bearer ${token}`,
      'X-Binance-API-Key': keys.apiKey,
      'X-Binance-API-Secret': keys.apiSecret,
    };
  };

  const saveKeys = () => {
    if (!apiKey || !apiSecret) return;
    stashBinanceKeys(apiKey, apiSecret);
    setHasKeys(true);
    setApiKey('');
    setApiSecret('');
    toast.success('Keys saved to this browser session only. Fetching balances...');
    setTimeout(fetchBalances, 100);
  };

  const testKeys = async () => {
    const headers = buildHeaders();
    if (!headers) return;
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/crypto/binance/test`, { headers });
      if (res.data.valid) toast.success('Binance keys are valid!');
      else toast.error(`Invalid keys: ${res.data.error}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to verify keys');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBalances = async () => {
    const headers = buildHeaders();
    if (!headers) return;
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/crypto/binance/balance`, { headers });
      setBalances(response.data.balances);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch balances');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDeposits = async () => {
    const headers = buildHeaders();
    if (!headers) return;
    try {
      const response = await axios.get(`${API}/crypto/binance/deposits`, { headers });
      setDeposits(response.data.deposits || []);
      // Fire threshold alert if any deposit exceeds threshold in last 24h
      const now = Date.now();
      const big = (response.data.deposits || []).filter(d =>
        parseFloat(d.amount) >= depositAlertThreshold &&
        (now - (d.insertTime || 0)) < 86400000
      );
      if (big.length > 0) {
        toast.warning(`${big.length} large deposit(s) detected above $${depositAlertThreshold}!`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch deposits');
    }
  };

  const disconnect = () => {
    if (!window.confirm('Disconnect Binance? Keys will be removed from this browser.')) return;
    clearBinanceKeys();
    setHasKeys(false);
    setBalances([]);
    setDeposits([]);
    toast.success('Disconnected. Keys cleared from this browser.');
  };

  useEffect(() => {
    if (hasKeys) fetchBalances();
  }, []); // eslint-disable-line

  return (
    <div className="space-y-6" data-testid="binance-tab">
      <h2 className="text-2xl font-bold text-white">Binance Integration</h2>

      <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
        <p className="text-emerald-300 text-sm">
          <Shield className="inline mr-2" size={16} />
          <strong>Zero-Server-Storage:</strong> Your API keys are stored only in this browser's session memory
          and passed as request headers. They are <strong>never written to our database</strong> and are cleared when you close this tab.
        </p>
      </div>

      {!hasKeys ? (
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-bold text-white mb-4">Connect Binance Account</h3>
          <p className="text-gray-400 mb-4">Use <strong>read-only</strong> API permissions for security. No withdrawal access is needed.</p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">API Key</label>
              <Input
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter API Key"
                data-testid="binance-api-key-input"
                className="bg-gray-900 border-gray-700 text-white font-mono"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">API Secret</label>
              <div className="relative">
                <Input
                  type={showSecret ? 'text' : 'password'}
                  value={apiSecret}
                  onChange={(e) => setApiSecret(e.target.value)}
                  placeholder="Enter API Secret"
                  data-testid="binance-api-secret-input"
                  className="bg-gray-900 border-gray-700 text-white font-mono pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                >
                  {showSecret ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button
              onClick={saveKeys}
              disabled={!apiKey || !apiSecret}
              data-testid="binance-connect-btn"
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              Connect (Session Only)
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-3">
            <Button onClick={fetchBalances} disabled={isLoading} className="bg-gray-700 hover:bg-gray-600" data-testid="binance-refresh-btn">
              {isLoading ? <Loader2 className="animate-spin mr-2" size={18} /> : <RefreshCw size={18} className="mr-2" />}
              Refresh Balances
            </Button>
            <Button onClick={fetchDeposits} className="bg-gray-700 hover:bg-gray-600" data-testid="binance-deposits-btn">
              View Deposits
            </Button>
            <Button onClick={testKeys} className="bg-blue-600 hover:bg-blue-700" data-testid="binance-test-btn">
              Test Keys
            </Button>
            <Button onClick={disconnect} variant="outline" className="border-red-500 text-red-400 hover:bg-red-500/10" data-testid="binance-disconnect-btn">
              Disconnect
            </Button>
          </div>

          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-white">Large Deposit Alert</h3>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400">Threshold ($)</span>
                <Input
                  type="number"
                  value={depositAlertThreshold}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value) || 0;
                    setDepositAlertThreshold(v);
                    localStorage.setItem('depositAlertThreshold', v);
                  }}
                  className="w-24 bg-gray-900 border-gray-700 text-white"
                />
              </div>
            </div>
            <p className="text-sm text-gray-400">Deposits above this amount will trigger an in-dashboard alert when you click "View Deposits".</p>
          </div>

          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-4">Balances</h3>
            {balances.length === 0 ? (
              <p className="text-gray-400">No balances found</p>
            ) : (
              <div className="grid md:grid-cols-3 gap-4">
                {balances.map((b) => (
                  <div key={b.asset} className="p-4 bg-gray-900 rounded-lg">
                    <p className="font-bold text-white">{b.asset}</p>
                    <p className="text-2xl font-bold text-yellow-400">{b.total.toFixed(8)}</p>
                    <p className="text-xs text-gray-400">Free: {b.free.toFixed(8)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {deposits.length > 0 && (
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <h3 className="text-lg font-bold text-white mb-4">Recent Deposits</h3>
              <div className="space-y-2">
                {deposits.slice(0, 10).map((d, i) => (
                  <div key={i} className="p-3 bg-gray-900 rounded-lg flex justify-between">
                    <span className="text-white">{d.amount} {d.coin}</span>
                    <span className="text-gray-400">{d.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Opportunities Tab - Airdrops (read-only, no claim execution)
const OpportunitiesTab = ({ token }) => {
  const [airdrops, setAirdrops] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchAirdrops();
  }, []);

  const fetchAirdrops = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/crypto/airdrops`);
      setAirdrops(response.data);
    } catch (err) {
      toast.error('Failed to fetch airdrops');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-purple-500" size={32} /></div>;
  }

  const priorityColor = {
    critical: 'bg-red-500/20 text-red-400 border-red-500/30',
    high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    low: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  };

  return (
    <div className="space-y-6" data-testid="opportunities-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Airdrop Opportunities</h2>
        <Button onClick={fetchAirdrops} className="bg-gray-700 hover:bg-gray-600">
          <RefreshCw size={18} className="mr-2" /> Refresh
        </Button>
      </div>
      <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
        <p className="text-purple-300 text-sm">
          <AlertCircle className="inline mr-2" size={16} />
          <strong>Read-only information only.</strong> This dashboard does NOT execute any claims. Always verify airdrop authenticity yourself.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {airdrops.map((airdrop) => (
          <div key={airdrop.id} className="p-5 bg-gray-800 rounded-xl border border-gray-700" data-testid={`airdrop-card-${airdrop.id}`}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <h4 className="font-bold text-white text-lg">{airdrop.title}</h4>
                <p className="text-xs text-gray-400 mt-1">{airdrop.project_name} · {airdrop.chain}</p>
              </div>
              <span className={`px-2 py-1 rounded text-xs border ${priorityColor[airdrop.priority] || priorityColor.medium}`}>
                {airdrop.priority}
              </span>
            </div>
            <p className="text-gray-400 text-sm mb-3">{airdrop.description}</p>
            {airdrop.estimated_value && (
              <p className="text-yellow-400 font-medium text-sm mb-3">Est. Value: {airdrop.estimated_value}</p>
            )}
            <div className="mb-3">
              <p className="text-xs text-gray-400 mb-1">Requirements:</p>
              <ul className="text-sm text-gray-300 space-y-1">
                {(airdrop.requirements || []).map((req, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle size={14} className="text-green-400 mt-0.5 flex-shrink-0" />
                    <span>{req}</span>
                  </li>
                ))}
              </ul>
            </div>
            {airdrop.link && (
              <a href={airdrop.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-purple-400 hover:text-purple-300 text-sm">
                Learn More <ExternalLink size={14} />
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Miners Tab - XMRig/CGMiner monitoring
const MinersTab = ({ token }) => {
  const [miners, setMiners] = useState([]);
  const [systemInfo, setSystemInfo] = useState(null);
  const [minerStatsMap, setMinerStatsMap] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [showAutoForm, setShowAutoForm] = useState(false);
  const [bestRanked, setBestRanked] = useState([]);
  const [autoLoading, setAutoLoading] = useState(false);
  const [binStatus, setBinStatus] = useState(null);
  const [showActiveForm, setShowActiveForm] = useState(false);
  const [activeData, setActiveData] = useState({
    name: 'AutoBest-01',
    wallet_address: '',
    hashrate: 1000,
    hashrateUnit: 'H',
    power_watts: 50,
    power_cost_kwh: 0.10,
  });
  const [autoData, setAutoData] = useState({
    name: 'AutoRig-01',
    wallet_address: '',
    hashrate: 1000000,
    hashrateUnit: 'MH',
    power_watts: 150,
    power_cost_kwh: 0.10,
  });
  const [formData, setFormData] = useState({
    name: '',
    miner_type: 'xmrig',
    algorithm: 'randomx',
    pool_url: 'stratum+tcp://monero.herominers.com:1111',
    wallet_address: '',
    worker_name: 'worker1',
    password: 'x',
    api_host: '127.0.0.1',
    api_port: 4444,
    api_token: '',
  });

  const headers = { Authorization: `Bearer ${token}` };
  const hashrateUnitMultiplier = { H: 1, KH: 1e3, MH: 1e6, GH: 1e9, TH: 1e12 };


  const load = async () => {
    try {
      const [minersRes, sysRes, binRes] = await Promise.all([
        axios.get(`${API}/crypto/mining/miners`, { headers }),
        axios.get(`${API}/crypto/mining/system-info`, { headers }),
        axios.get(`${API}/crypto/mining/binaries/status`, { headers }),
      ]);
      setMiners(minersRes.data);
      setSystemInfo(sysRes.data);
      setBinStatus(binRes.data);
    } catch (err) {
      toast.error('Failed to load miners');
    }
  };

  const startMiner = async (id) => {
    try {
      const res = await axios.post(`${API}/crypto/mining/miners/${id}/start`, {}, { headers });
      toast.success(`Miner started (PID ${res.data.pid})`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Start failed');
    }
  };

  const stopMiner = async (id) => {
    try {
      await axios.post(`${API}/crypto/mining/miners/${id}/stop`, {}, { headers });
      toast.success('Miner stopped');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Stop failed');
    }
  };

  const startBest = async () => {
    if (!activeData.wallet_address) { toast.error('Wallet address is required'); return; }
    setAutoLoading(true);
    try {
      const hr = activeData.hashrate * (hashrateUnitMultiplier[activeData.hashrateUnit] || 1);
      const res = await axios.post(`${API}/crypto/mining/start-best`, {
        name: activeData.name,
        wallet_address: activeData.wallet_address,
        hashrate: hr,
        power_watts: activeData.power_watts,
        power_cost_kwh: activeData.power_cost_kwh,
      }, { headers });
      toast.success(res.data.message);
      setShowActiveForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Start-best failed', { duration: 8000 });
    } finally {
      setAutoLoading(false);
    }
  };

  const fetchStats = async (minerId) => {
    try {
      const res = await axios.get(`${API}/crypto/mining/miners/${minerId}/stats`, { headers });
      setMinerStatsMap(prev => ({ ...prev, [minerId]: res.data }));
    } catch (err) {
      setMinerStatsMap(prev => ({ ...prev, [minerId]: { error: err.response?.data?.detail || 'Unreachable' } }));
    }
  };

  const addMiner = async () => {
    try {
      const payload = { ...formData, api_token: formData.api_token || null };
      await axios.post(`${API}/crypto/mining/miners`, payload, { headers });
      toast.success('Miner added');
      setShowForm(false);
      setFormData({ ...formData, name: '', wallet_address: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add miner');
    }
  };

  const deleteMiner = async (id) => {
    if (!window.confirm('Remove this miner?')) return;
    try {
      await axios.delete(`${API}/crypto/mining/miners/${id}`, { headers });
      toast.success('Miner removed');
      load();
    } catch (err) {
      toast.error('Failed to remove miner');
    }
  };


  const rankBest = async () => {
    setAutoLoading(true);
    try {
      const hr = autoData.hashrate * (hashrateUnitMultiplier[autoData.hashrateUnit] || 1);
      const res = await axios.post(`${API}/crypto/mining/best-coins`, {
        hashrate: hr,
        power_watts: autoData.power_watts,
        power_cost_kwh: autoData.power_cost_kwh,
      }, { headers });
      setBestRanked(res.data.ranked || []);
      toast.success(`Top coin: ${res.data.top?.coin} @ $${res.data.top?.daily_profit_usd}/day`);
    } catch (err) {
      toast.error('Failed to rank coins');
    } finally {
      setAutoLoading(false);
    }
  };

  const autoProvision = async () => {
    if (!autoData.wallet_address) { toast.error('Wallet address is required'); return; }
    setAutoLoading(true);
    try {
      const hr = autoData.hashrate * (hashrateUnitMultiplier[autoData.hashrateUnit] || 1);
      const res = await axios.post(`${API}/crypto/mining/auto-provision`, {
        name: autoData.name,
        wallet_address: autoData.wallet_address,
        hashrate: hr,
        power_watts: autoData.power_watts,
        power_cost_kwh: autoData.power_cost_kwh,
      }, { headers });
      toast.success(`Auto-provisioned for ${res.data.chosen_coin} on ${res.data.pool?.name}`);
      setShowAutoForm(false);
      setBestRanked(res.data.top_3_ranked || []);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-provision failed');
    } finally {
      setAutoLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="space-y-6" data-testid="miners-tab">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h2 className="text-2xl font-bold text-white">Mining Rigs & Workers</h2>
        <div className="flex gap-2 flex-wrap">
          {binStatus?.launch_enabled && (
            <Button onClick={() => setShowActiveForm(!showActiveForm)} className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white" data-testid="start-mining-btn">
              <Zap size={18} className="mr-2" /> Start Mining (Auto)
            </Button>
          )}
          <Button onClick={() => setShowAutoForm(!showAutoForm)} className="bg-gradient-to-r from-amber-500 to-pink-600 hover:from-amber-600 hover:to-pink-700 text-white" data-testid="auto-suggest-btn">
            <Sparkles size={18} className="mr-2" /> Auto-Suggest Best
          </Button>
          <Button onClick={() => setShowForm(!showForm)} className="bg-purple-600 hover:bg-purple-700" data-testid="add-miner-btn">
            <Plus size={18} className="mr-2" /> Add Miner
          </Button>
        </div>
      </div>

      {binStatus && !binStatus.launch_enabled && (
        <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg" data-testid="launch-disabled-banner">
          <p className="text-blue-300 text-sm">
            <Shield className="inline mr-2" size={16} />
            <strong>Active mining is disabled on this hosted preview.</strong> To actually run miners (XMRig, etc.) from
            this dashboard, self-host on your mining rig with <code className="bg-gray-900 px-1.5 py-0.5 rounded text-xs">MINER_LAUNCH_ENABLED=true</code> in your <code className="bg-gray-900 px-1.5 py-0.5 rounded text-xs">.env</code>. See README for the full Docker Compose setup.
          </p>
        </div>
      )}

      {binStatus?.launch_enabled && showActiveForm && (
        <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl p-6 border border-green-500/30 space-y-3">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Zap className="text-green-400" /> Start Mining the Most Profitable Coin
          </h3>
          <p className="text-sm text-gray-300">
            One-click setup: we'll auto-download XMRig (~50 MB), pick the most profitable RandomX coin live, and launch it.
            Your earnings go to the wallet address you provide.
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            <Input placeholder="Miner Name" value={activeData.name} onChange={e => setActiveData({...activeData, name: e.target.value})} className="bg-gray-900 border-gray-700 text-white" data-testid="active-name-input" />
            <Input placeholder="Wallet Address (where you receive payouts)" value={activeData.wallet_address} onChange={e => setActiveData({...activeData, wallet_address: e.target.value})} className="bg-gray-900 border-gray-700 text-white" data-testid="active-wallet-input" />
            <div className="flex gap-2">
              <Input type="number" value={activeData.hashrate} onChange={e => setActiveData({...activeData, hashrate: parseFloat(e.target.value) || 0})} className="bg-gray-900 border-gray-700 text-white flex-1" placeholder="Hashrate (estimate)" />
              <select value={activeData.hashrateUnit} onChange={e => setActiveData({...activeData, hashrateUnit: e.target.value})} className="px-3 bg-gray-900 border border-gray-700 rounded text-white">
                {Object.keys(hashrateUnitMultiplier).map(u => <option key={u} value={u}>{u}/s</option>)}
              </select>
            </div>
            <Input type="number" placeholder="Power Watts" value={activeData.power_watts} onChange={e => setActiveData({...activeData, power_watts: parseFloat(e.target.value) || 0})} className="bg-gray-900 border-gray-700 text-white" />
            <Input type="number" step="0.01" placeholder="Power Cost $/kWh" value={activeData.power_cost_kwh} onChange={e => setActiveData({...activeData, power_cost_kwh: parseFloat(e.target.value) || 0})} className="bg-gray-900 border-gray-700 text-white md:col-span-2" />
          </div>
          <Button onClick={startBest} disabled={autoLoading || !activeData.wallet_address} className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 w-full" data-testid="start-best-btn">
            {autoLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Zap size={16} className="mr-2" />}
            Download XMRig & Start Mining Best Coin
          </Button>
        </div>
      )}

      {systemInfo && (
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-bold text-white mb-4">Local System</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 bg-gray-900 rounded-lg">
              <p className="text-xs text-gray-400">CPU Usage</p>
              <p className="text-xl font-bold text-cyan-400">{systemInfo.system?.cpu_percent?.toFixed(1)}%</p>
            </div>
            <div className="p-3 bg-gray-900 rounded-lg">
              <p className="text-xs text-gray-400">CPU Cores</p>
              <p className="text-xl font-bold text-white">{systemInfo.system?.cpu_count}</p>
            </div>
            <div className="p-3 bg-gray-900 rounded-lg">
              <p className="text-xs text-gray-400">Memory</p>
              <p className="text-xl font-bold text-white">{systemInfo.system?.memory_used_gb?.toFixed(1)} / {systemInfo.system?.memory_total_gb?.toFixed(1)} GB</p>
            </div>
            <div className="p-3 bg-gray-900 rounded-lg">
              <p className="text-xs text-gray-400">GPUs</p>
              <p className="text-xl font-bold text-white">{systemInfo.system?.gpus?.length || 0}</p>
            </div>
          </div>
        </div>
      )}

      {showAutoForm && (
        <div className="bg-gradient-to-br from-amber-500/10 to-pink-600/10 rounded-xl p-6 border border-amber-500/30 space-y-3">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Sparkles className="text-amber-400" /> Auto-Suggest Most Profitable Coin
          </h3>
          <p className="text-sm text-gray-300">Enter your rig specs. We'll rank built-in + your custom coins by live daily profit, then one-click provision the best pool config.</p>
          <div className="grid md:grid-cols-2 gap-3">
            <Input placeholder="Miner Name" value={autoData.name} onChange={e => setAutoData({...autoData, name: e.target.value})} className="bg-gray-900 border-gray-700 text-white" data-testid="auto-name-input" />
            <Input placeholder="Wallet Address (where you receive payouts)" value={autoData.wallet_address} onChange={e => setAutoData({...autoData, wallet_address: e.target.value})} className="bg-gray-900 border-gray-700 text-white" data-testid="auto-wallet-input" />
            <div className="flex gap-2">
              <Input type="number" value={autoData.hashrate} onChange={e => setAutoData({...autoData, hashrate: parseFloat(e.target.value) || 0})} className="bg-gray-900 border-gray-700 text-white flex-1" placeholder="Hashrate" data-testid="auto-hashrate-input" />
              <select value={autoData.hashrateUnit} onChange={e => setAutoData({...autoData, hashrateUnit: e.target.value})} className="px-3 bg-gray-900 border border-gray-700 rounded text-white">
                {Object.keys(hashrateUnitMultiplier).map(u => <option key={u} value={u}>{u}/s</option>)}
              </select>
            </div>
            <Input type="number" placeholder="Power Watts" value={autoData.power_watts} onChange={e => setAutoData({...autoData, power_watts: parseFloat(e.target.value) || 0})} className="bg-gray-900 border-gray-700 text-white" data-testid="auto-power-input" />
            <Input type="number" step="0.01" placeholder="Power Cost $/kWh" value={autoData.power_cost_kwh} onChange={e => setAutoData({...autoData, power_cost_kwh: parseFloat(e.target.value) || 0})} className="bg-gray-900 border-gray-700 text-white md:col-span-2" data-testid="auto-cost-input" />
          </div>
          <div className="flex gap-2">
            <Button onClick={rankBest} disabled={autoLoading} variant="outline" className="border-amber-500 text-amber-400 hover:bg-amber-500/10">
              {autoLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Rank All Coins
            </Button>
            <Button onClick={autoProvision} disabled={autoLoading || !autoData.wallet_address} className="bg-gradient-to-r from-amber-500 to-pink-600 hover:from-amber-600 hover:to-pink-700" data-testid="auto-provision-btn">
              {autoLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Zap size={16} className="mr-2" />}
              Auto-Provision Best
            </Button>
          </div>
          {bestRanked.length > 0 && (
            <div className="mt-4 bg-gray-900 rounded-lg p-4">
              <p className="text-sm text-gray-400 mb-2">Top-ranked coins (live data):</p>
              <div className="space-y-1">
                {bestRanked.slice(0, 5).map((r, i) => (
                  <div key={r.coin} className="flex justify-between items-center p-2 bg-gray-800 rounded" data-testid={`rank-row-${r.coin}`}>
                    <div className="flex items-center gap-3">
                      <span className="text-amber-400 font-bold">#{i+1}</span>
                      <span className="text-white font-medium">{r.coin}</span>
                      <span className="text-xs text-gray-500">{r.algorithm}</span>
                      {r.live_data && <span className="text-xs px-1.5 py-0.5 bg-green-500/20 text-green-400 rounded">LIVE</span>}
                      {r.is_custom && <span className="text-xs px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded">CUSTOM</span>}
                    </div>
                    <span className={`font-bold ${r.daily_profit_usd >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      ${r.daily_profit_usd.toFixed(4)}/day
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 space-y-3">
          <h3 className="text-lg font-bold text-white">New Miner Configuration</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <Input placeholder="Miner Name (e.g., Rig-01)" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="bg-gray-900 border-gray-700 text-white" data-testid="miner-name-input" />
            <select value={formData.miner_type} onChange={e => setFormData({...formData, miner_type: e.target.value})} className="px-4 py-2 bg-gray-900 border border-gray-700 rounded text-white">
              <option value="xmrig">XMRig</option>
              <option value="cgminer">CGMiner</option>
              <option value="bfgminer">BFGMiner</option>
              <option value="easyminer">EasyMiner</option>
            </select>
            <select value={formData.algorithm} onChange={e => setFormData({...formData, algorithm: e.target.value})} className="px-4 py-2 bg-gray-900 border border-gray-700 rounded text-white">
              <option value="randomx">RandomX (XMR)</option>
              <option value="sha256">SHA-256 (BTC)</option>
              <option value="scrypt">Scrypt (LTC/DOGE)</option>
              <option value="ethash">Ethash</option>
              <option value="etchash">Etchash (ETC)</option>
              <option value="kawpow">KawPow (RVN)</option>
              <option value="autolykos2">Autolykos2 (ERG)</option>
              <option value="kheavyhash">kHeavyHash (KAS)</option>
            </select>
            <Input placeholder="Pool URL" value={formData.pool_url} onChange={e => setFormData({...formData, pool_url: e.target.value})} className="bg-gray-900 border-gray-700 text-white" />
            <Input placeholder="Wallet Address" value={formData.wallet_address} onChange={e => setFormData({...formData, wallet_address: e.target.value})} className="bg-gray-900 border-gray-700 text-white md:col-span-2" data-testid="miner-wallet-input" />
            <Input placeholder="Worker Name" value={formData.worker_name} onChange={e => setFormData({...formData, worker_name: e.target.value})} className="bg-gray-900 border-gray-700 text-white" />
            <Input placeholder="Password" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} className="bg-gray-900 border-gray-700 text-white" />
            <Input placeholder="API Host (127.0.0.1)" value={formData.api_host} onChange={e => setFormData({...formData, api_host: e.target.value})} className="bg-gray-900 border-gray-700 text-white" />
            <Input type="number" placeholder="API Port (4444)" value={formData.api_port} onChange={e => setFormData({...formData, api_port: parseInt(e.target.value)})} className="bg-gray-900 border-gray-700 text-white" />
            <Input placeholder="API Token (optional)" value={formData.api_token} onChange={e => setFormData({...formData, api_token: e.target.value})} className="bg-gray-900 border-gray-700 text-white md:col-span-2" />
          </div>
          <Button onClick={addMiner} className="bg-gradient-to-r from-purple-600 to-cyan-600" data-testid="save-miner-btn">Save Miner</Button>
        </div>
      )}

      {miners.length === 0 ? (
        <div className="bg-gray-800 rounded-xl p-12 border border-gray-700 text-center">
          <Cpu size={48} className="mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400">No miners configured yet. Click "Auto-Suggest Best" or "Add Miner" to register one.</p>
          <p className="text-gray-500 text-sm mt-2">The dashboard connects to your local XMRig/CGMiner HTTP/RPC API to read live stats.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {miners.map(m => (
            <div key={m.id} className="bg-gray-800 rounded-xl p-5 border border-gray-700" data-testid={`miner-row-${m.id}`}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="font-bold text-white text-lg">{m.name}
                    {m.auto_provisioned_for_coin && <span className="ml-2 text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded">AUTO: {m.auto_provisioned_for_coin}</span>}
                    {m.is_running && <span className="ml-2 text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded animate-pulse">● RUNNING</span>}
                  </h4>
                  <p className="text-xs text-gray-400">{m.miner_type} · {m.algorithm} · {m.api_host}:{m.api_port}</p>
                </div>
                <div className="flex gap-2">
                  {binStatus?.launch_enabled && (
                    m.is_running ? (
                      <Button size="sm" onClick={() => stopMiner(m.id)} className="bg-red-600 hover:bg-red-700 text-white" data-testid={`stop-${m.id}`}>
                        Stop
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => startMiner(m.id)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`start-${m.id}`}>
                        Start
                      </Button>
                    )
                  )}
                  <Button size="sm" onClick={() => fetchStats(m.id)} className="bg-gray-700 hover:bg-gray-600">Check Stats</Button>
                  <Button size="sm" onClick={() => deleteMiner(m.id)} className="bg-red-600/30 hover:bg-red-600/50 text-red-300">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
              {minerStatsMap[m.id] && (
                <div className="mt-3 p-3 bg-gray-900 rounded">
                  {minerStatsMap[m.id].error ? (
                    <p className="text-red-400 text-sm">{minerStatsMap[m.id].error}</p>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div><p className="text-gray-400 text-xs">Status</p><p className="text-white">{minerStatsMap[m.id].status}</p></div>
                      <div><p className="text-gray-400 text-xs">Hashrate</p><p className="text-cyan-400 font-bold">{minerStatsMap[m.id].total_hashrate?.toFixed(2)} H/s</p></div>
                      <div><p className="text-gray-400 text-xs">Accepted</p><p className="text-green-400">{minerStatsMap[m.id].shares_accepted}</p></div>
                      <div><p className="text-gray-400 text-xs">Rejected</p><p className="text-red-400">{minerStatsMap[m.id].shares_rejected}</p></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Arbitrage Tab - Price spreads between exchanges
const ArbitrageTab = ({ token }) => {
  const [opps, setOpps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const load = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/crypto/arbitrage`);
      setOpps(res.data);
    } catch (err) {
      toast.error('Failed to scan arbitrage');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="arbitrage-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Arbitrage Opportunities</h2>
        <Button onClick={load} className="bg-gray-700 hover:bg-gray-600">
          <RefreshCw size={18} className="mr-2" /> Rescan
        </Button>
      </div>

      <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
        <p className="text-cyan-300 text-sm">
          <Zap className="inline mr-2" size={16} />
          Spreads between <strong>Binance</strong> and <strong>KuCoin</strong> public price feeds (free tier). Minimum 1% spread shown. Actual executable spread depends on exchange fees, slippage, and withdrawal costs.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-cyan-500" size={32} /></div>
      ) : opps.length === 0 ? (
        <div className="bg-gray-800 rounded-xl p-12 text-center border border-gray-700">
          <p className="text-gray-400">No arbitrage opportunities above 1% right now.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {opps.map(o => (
            <div key={o.id} className="bg-gray-800 rounded-xl p-5 border border-gray-700" data-testid={`arb-row-${o.coin}`}>
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="font-bold text-white text-lg">{o.coin}</h4>
                  <p className="text-sm text-gray-400">{o.description}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-400">+{o.spread_percent.toFixed(2)}%</p>
                  <p className="text-xs text-gray-400">spread</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div className="p-3 bg-gray-900 rounded">
                  <p className="text-xs text-gray-400">Buy on {o.buy_exchange}</p>
                  <p className="text-white font-bold">${o.buy_price.toFixed(4)}</p>
                </div>
                <div className="p-3 bg-gray-900 rounded">
                  <p className="text-xs text-gray-400">Sell on {o.sell_exchange}</p>
                  <p className="text-white font-bold">${o.sell_price.toFixed(4)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Wallet Scan Tab - Etherscan-based dust & token analyzer
const WalletScanTab = ({ token }) => {
  const [address, setAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const scan = async () => {
    if (!address.startsWith('0x') || address.length !== 42) {
      toast.error('Enter a valid Ethereum address (0x...)');
      return;
    }
    setIsLoading(true);
    setAnalysis(null);
    try {
      const res = await axios.post(`${API}/crypto/wallet/analyze`, { address }, { headers });
      setAnalysis(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="wallet-scan-tab">
      <h2 className="text-2xl font-bold text-white">Wallet Dust & Token Scanner</h2>

      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <p className="text-gray-400 text-sm mb-3">Analyze any Ethereum wallet for dust tokens and potential scam tokens using Etherscan's free tier.</p>
        <div className="flex gap-3">
          <Input
            placeholder="0x..."
            value={address}
            onChange={e => setAddress(e.target.value)}
            className="bg-gray-900 border-gray-700 text-white font-mono"
            data-testid="wallet-address-input"
          />
          <Button onClick={scan} disabled={isLoading} className="bg-gradient-to-r from-purple-600 to-cyan-600" data-testid="wallet-scan-btn">
            {isLoading ? <Loader2 className="animate-spin mr-2" size={18} /> : <Search size={18} className="mr-2" />}
            Scan
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-2">Scanning can take up to 30-60 seconds due to per-token balance checks.</p>
      </div>

      {analysis && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-800 rounded-xl border border-gray-700">
              <p className="text-xs text-gray-400">ETH Balance</p>
              <p className="text-xl font-bold text-cyan-400">{analysis.eth_balance.toFixed(6)}</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-xl border border-gray-700">
              <p className="text-xs text-gray-400">Token Count</p>
              <p className="text-xl font-bold text-white">{analysis.token_count}</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-xl border border-gray-700">
              <p className="text-xs text-gray-400">Dust Tokens</p>
              <p className="text-xl font-bold text-yellow-400">{analysis.dust_tokens?.length || 0}</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-xl border border-gray-700">
              <p className="text-xs text-gray-400">Scam Risk</p>
              <p className="text-xl font-bold text-red-400">{analysis.dust_tokens?.filter(t => t.is_scam_risk).length || 0}</p>
            </div>
          </div>

          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-4">Tokens Detected</h3>
            {!analysis.dust_tokens?.length ? (
              <p className="text-gray-400">No tokens found.</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {analysis.dust_tokens.map(t => (
                  <div key={t.contract_address} className="p-3 bg-gray-900 rounded flex justify-between items-center">
                    <div>
                      <p className="font-bold text-white">{t.token_symbol} <span className="text-xs text-gray-400">{t.token_name}</span></p>
                      <p className="text-xs text-gray-500 font-mono">{t.contract_address.slice(0,10)}...{t.contract_address.slice(-8)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white">{t.balance.toFixed(6)}</p>
                      {t.is_scam_risk && <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded">⚠ scam-risk</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Alerts Tab - Price Alerts
const AlertsTab = ({ token, prices }) => {
  const [alerts, setAlerts] = useState([]);
  const [form, setForm] = useState({ coin: 'BTC', condition: 'above', target_price: 0 });
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/crypto/alerts`, { headers });
      setAlerts(res.data);
    } catch { /* silent */ }
  };

  const create = async () => {
    if (!form.target_price || form.target_price <= 0) {
      toast.error('Target price must be > 0');
      return;
    }
    try {
      await axios.post(`${API}/crypto/alerts`, form, { headers });
      toast.success('Alert created');
      setForm({ ...form, target_price: 0 });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create alert');
    }
  };

  const del = async (id) => {
    try {
      await axios.delete(`${API}/crypto/alerts/${id}`, { headers });
      load();
    } catch { toast.error('Failed'); }
  };

  const check = async () => {
    try {
      const res = await axios.post(`${API}/crypto/alerts/check`, {}, { headers });
      if (res.data.triggered?.length > 0) {
        toast.success(`${res.data.triggered.length} alert(s) triggered!`);
      } else {
        toast.info(`Checked ${res.data.checked} alert(s). None triggered.`);
      }
      load();
    } catch { toast.error('Check failed'); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const coins = Object.keys(prices);

  return (
    <div className="space-y-6" data-testid="alerts-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Price Alerts</h2>
        <Button onClick={check} className="bg-gray-700 hover:bg-gray-600" data-testid="check-alerts-btn">
          <Bell size={18} className="mr-2" /> Check Now
        </Button>
      </div>

      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <h3 className="text-lg font-bold text-white mb-3">Create Alert</h3>
        <div className="grid md:grid-cols-4 gap-3">
          <select value={form.coin} onChange={e => setForm({...form, coin: e.target.value})} className="px-4 py-2 bg-gray-900 border border-gray-700 rounded text-white">
            {coins.length === 0 ? <option value="BTC">BTC</option> : coins.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={form.condition} onChange={e => setForm({...form, condition: e.target.value})} className="px-4 py-2 bg-gray-900 border border-gray-700 rounded text-white">
            <option value="above">Above</option>
            <option value="below">Below</option>
          </select>
          <Input type="number" placeholder="Target price USD" value={form.target_price} onChange={e => setForm({...form, target_price: parseFloat(e.target.value) || 0})} className="bg-gray-900 border-gray-700 text-white" data-testid="alert-price-input" />
          <Button onClick={create} className="bg-purple-600 hover:bg-purple-700" data-testid="create-alert-btn">
            <Plus size={18} className="mr-2" /> Add
          </Button>
        </div>
      </div>

      {alerts.length === 0 ? (
        <div className="bg-gray-800 rounded-xl p-12 text-center border border-gray-700">
          <p className="text-gray-400">No alerts yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {alerts.map(a => (
            <div key={a.id} className="bg-gray-800 rounded-xl p-4 border border-gray-700 flex justify-between items-center" data-testid={`alert-row-${a.id}`}>
              <div>
                <p className="font-bold text-white">
                  {a.coin} {a.condition} <span className="text-yellow-400">${a.target_price}</span>
                </p>
                <p className="text-xs text-gray-400">
                  Current: ${prices[a.coin]?.price_usd?.toFixed(2) || '?'}
                  {a.is_triggered && <span className="ml-2 text-green-400">✓ TRIGGERED</span>}
                </p>
              </div>
              <Button size="sm" onClick={() => del(a.id)} className="bg-red-600/30 hover:bg-red-600/50 text-red-300">
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Settings Tab
const SettingsTab = ({ user, token, setUser }) => {
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const setup2FA = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/crypto/setup-2fa`, {}, { headers });
      setQrCode(response.data.qr_code);
      setSecret(response.data.secret);
    } catch (err) {
      toast.error('Failed to setup 2FA');
    } finally {
      setIsLoading(false);
    }
  };

  const verify2FA = async () => {
    try {
      await axios.post(`${API}/crypto/verify-2fa?code=${verifyCode}`, {}, { headers });
      setUser(prev => ({ ...prev, totp_enabled: true }));
      setQrCode(null);
      setSecret('');
      setVerifyCode('');
      toast.success('2FA enabled successfully!');
    } catch (err) {
      toast.error('Invalid code');
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-white">Settings</h2>
      
      {/* 2FA Setup */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Shield className="text-green-400" /> Two-Factor Authentication
        </h3>
        
        {user?.totp_enabled ? (
          <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
            <CheckCircle className="text-green-400" size={24} />
            <div>
              <p className="font-medium text-green-400">2FA is enabled</p>
              <p className="text-sm text-gray-400">Your account is protected with Google Authenticator</p>
            </div>
          </div>
        ) : qrCode ? (
          <div className="space-y-4">
            <p className="text-gray-400">Scan this QR code with Google Authenticator:</p>
            <div className="flex justify-center">
              <img src={qrCode} alt="2FA QR Code" className="rounded-lg" />
            </div>
            <div className="p-3 bg-gray-900 rounded-lg">
              <p className="text-xs text-gray-400 mb-1">Manual entry code:</p>
              <div className="flex items-center gap-2">
                <code className="text-yellow-400 font-mono">{secret}</code>
                <button onClick={() => { navigator.clipboard.writeText(secret); toast.success('Copied!'); }} className="p-1 hover:bg-gray-800 rounded">
                  <Copy size={16} className="text-gray-400" />
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Enter code from app to verify:</label>
              <div className="flex gap-2">
                <Input
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value)}
                  placeholder="000000"
                  className="bg-gray-900 border-gray-700 text-white text-center tracking-widest text-lg"
                  maxLength={6}
                />
                <Button onClick={verify2FA} className="bg-green-600 hover:bg-green-700">Verify & Enable</Button>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-gray-400 mb-4">Add an extra layer of security to your account</p>
            <Button onClick={setup2FA} disabled={isLoading} className="bg-purple-600 hover:bg-purple-700">
              {isLoading ? <Loader2 className="animate-spin mr-2" size={18} /> : <QrCode className="mr-2" size={18} />}
              Setup 2FA
            </Button>
          </div>
        )}
      </div>

      {/* Account Info */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <h3 className="text-lg font-bold text-white mb-4">Account Information</h3>
        <div className="space-y-3">
          <div className="flex justify-between p-3 bg-gray-900 rounded-lg">
            <span className="text-gray-400">Username</span>
            <span className="text-white font-medium">{user?.username}</span>
          </div>
          <div className="flex justify-between p-3 bg-gray-900 rounded-lg">
            <span className="text-gray-400">Email</span>
            <span className="text-white font-medium">{user?.email}</span>
          </div>
          <div className="flex justify-between p-3 bg-gray-900 rounded-lg">
            <span className="text-gray-400">Tracked Wallets</span>
            <span className="text-white font-medium">{user?.watched_wallets?.length || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ Schedules Tab — Mining time-window + auto-switch rules ============
const SchedulesTab = ({ token }) => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [scheduleType, setScheduleType] = useState('time_window');
  const [tw, setTw] = useState({ name: 'Night Mining', start_hour_utc: 22, end_hour_utc: 6, miner_id: '' });
  const [as_, setAs] = useState({ name: 'Best Every 4h', interval_hours: 4, wallet_address: '', hashrate: 1000, hashrateUnit: 'H', power_watts: 50, power_cost_kwh: 0.10 });
  const headers = { Authorization: `Bearer ${token}` };
  const hashUnit = { H: 1, KH: 1e3, MH: 1e6, GH: 1e9, TH: 1e12 };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/crypto/schedules`, { headers });
      setItems(res.data);
    } catch { /* silent */ }
  };

  const create = async () => {
    let payload;
    if (scheduleType === 'time_window') {
      payload = { name: tw.name, schedule_type: 'time_window', start_hour_utc: parseInt(tw.start_hour_utc), end_hour_utc: parseInt(tw.end_hour_utc), miner_id: tw.miner_id || null };
    } else {
      if (!as_.wallet_address) { toast.error('Wallet address required'); return; }
      payload = {
        name: as_.name, schedule_type: 'auto_switch',
        interval_hours: parseInt(as_.interval_hours),
        wallet_address: as_.wallet_address,
        hashrate: as_.hashrate * (hashUnit[as_.hashrateUnit] || 1),
        power_watts: parseFloat(as_.power_watts),
        power_cost_kwh: parseFloat(as_.power_cost_kwh),
      };
    }
    try {
      await axios.post(`${API}/crypto/schedules`, payload, { headers });
      toast.success('Schedule created — first tick in <60s');
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    }
  };

  const toggle = async (s) => {
    try {
      await axios.patch(`${API}/crypto/schedules/${s.id}`, { enabled: !s.enabled }, { headers });
      load();
    } catch { toast.error('Toggle failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete schedule?')) return;
    try { await axios.delete(`${API}/crypto/schedules/${id}`, { headers }); load(); }
    catch { toast.error('Delete failed'); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="space-y-6" data-testid="schedules-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Mining Schedules</h2>
        <Button onClick={() => setShowForm(!showForm)} className="bg-purple-600 hover:bg-purple-700" data-testid="add-schedule-btn">
          <Plus size={18} className="mr-2" /> New Schedule
        </Button>
      </div>

      <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
        <p className="text-purple-300 text-sm">
          <Clock className="inline mr-2" size={16} />
          Schedules run every minute in the background. <strong>Time Window</strong>: only mine during specific UTC hours (e.g., 22:00–06:00 for cheap night electricity).
          <strong> Auto-Switch</strong>: every N hours, automatically swap to whichever RandomX coin is most profitable right now.
          On cloud-hosted previews where launching is disabled, schedules will create <strong>recommendations as notifications</strong> instead of executing.
        </p>
      </div>

      {showForm && (
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 space-y-4">
          <div className="flex gap-2">
            <Button onClick={() => setScheduleType('time_window')} className={scheduleType === 'time_window' ? 'bg-purple-600' : 'bg-gray-700'} data-testid="schedule-type-time">
              <Clock size={14} className="mr-2" /> Time Window
            </Button>
            <Button onClick={() => setScheduleType('auto_switch')} className={scheduleType === 'auto_switch' ? 'bg-purple-600' : 'bg-gray-700'} data-testid="schedule-type-auto">
              <Sparkles size={14} className="mr-2" /> Auto-Switch
            </Button>
          </div>

          {scheduleType === 'time_window' ? (
            <div className="grid md:grid-cols-2 gap-3">
              <Input placeholder="Schedule Name" value={tw.name} onChange={e => setTw({...tw, name: e.target.value})} className="bg-gray-900 border-gray-700 text-white md:col-span-2" data-testid="tw-name-input" />
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Start hour (0-23 UTC)</label>
                <Input type="number" min="0" max="23" value={tw.start_hour_utc} onChange={e => setTw({...tw, start_hour_utc: e.target.value})} className="bg-gray-900 border-gray-700 text-white" data-testid="tw-start-input" />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">End hour (0-23 UTC)</label>
                <Input type="number" min="0" max="23" value={tw.end_hour_utc} onChange={e => setTw({...tw, end_hour_utc: e.target.value})} className="bg-gray-900 border-gray-700 text-white" data-testid="tw-end-input" />
              </div>
              <p className="text-xs text-gray-500 md:col-span-2">Tip: end-hour can be lower than start-hour for overnight windows (e.g., 22→6 means mine from 22:00 to 06:00).</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              <Input placeholder="Schedule Name" value={as_.name} onChange={e => setAs({...as_, name: e.target.value})} className="bg-gray-900 border-gray-700 text-white" data-testid="as-name-input" />
              <Input type="number" min="1" placeholder="Interval (hours)" value={as_.interval_hours} onChange={e => setAs({...as_, interval_hours: e.target.value})} className="bg-gray-900 border-gray-700 text-white" data-testid="as-interval-input" />
              <Input placeholder="Wallet Address" value={as_.wallet_address} onChange={e => setAs({...as_, wallet_address: e.target.value})} className="bg-gray-900 border-gray-700 text-white md:col-span-2" data-testid="as-wallet-input" />
              <div className="flex gap-2">
                <Input type="number" placeholder="Hashrate" value={as_.hashrate} onChange={e => setAs({...as_, hashrate: parseFloat(e.target.value) || 0})} className="bg-gray-900 border-gray-700 text-white flex-1" />
                <select value={as_.hashrateUnit} onChange={e => setAs({...as_, hashrateUnit: e.target.value})} className="px-2 bg-gray-900 border border-gray-700 rounded text-white">
                  {Object.keys(hashUnit).map(u => <option key={u} value={u}>{u}/s</option>)}
                </select>
              </div>
              <Input type="number" placeholder="Power Watts" value={as_.power_watts} onChange={e => setAs({...as_, power_watts: e.target.value})} className="bg-gray-900 border-gray-700 text-white" />
              <Input type="number" step="0.01" placeholder="$/kWh" value={as_.power_cost_kwh} onChange={e => setAs({...as_, power_cost_kwh: e.target.value})} className="bg-gray-900 border-gray-700 text-white md:col-span-2" />
            </div>
          )}
          <Button onClick={create} className="bg-gradient-to-r from-purple-600 to-cyan-600" data-testid="save-schedule-btn">
            Save Schedule
          </Button>
        </div>
      )}

      {items.length === 0 ? (
        <div className="bg-gray-800 rounded-xl p-12 text-center border border-gray-700">
          <Clock size={48} className="mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400">No schedules yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(s => (
            <div key={s.id} className="bg-gray-800 rounded-xl p-4 border border-gray-700" data-testid={`schedule-row-${s.id}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-white">{s.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${s.schedule_type === 'time_window' ? 'bg-blue-500/20 text-blue-400' : 'bg-amber-500/20 text-amber-400'}`}>
                      {s.schedule_type === 'time_window' ? 'TIME WINDOW' : 'AUTO-SWITCH'}
                    </span>
                    {s.enabled ? (
                      <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">ENABLED</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded bg-gray-700 text-gray-400">PAUSED</span>
                    )}
                  </div>
                  {s.schedule_type === 'time_window' ? (
                    <p className="text-sm text-gray-400">
                      Mine from {String(s.start_hour_utc).padStart(2, '0')}:00 to {String(s.end_hour_utc).padStart(2, '0')}:00 UTC
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400">
                      Re-evaluate best coin every {s.interval_hours}h · {s.hashrate?.toLocaleString()} H/s @ {s.power_watts}W
                    </p>
                  )}
                  {s.last_action && (
                    <p className="text-xs text-gray-500 mt-1">
                      Last: <span className="text-cyan-400">{s.last_action}</span> at {s.last_action_at ? new Date(s.last_action_at).toLocaleString() : '—'}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => toggle(s)} className={s.enabled ? "bg-amber-600/30 hover:bg-amber-600/50 text-amber-300" : "bg-green-600/30 hover:bg-green-600/50 text-green-300"} data-testid={`toggle-${s.id}`}>
                    {s.enabled ? 'Pause' : 'Resume'}
                  </Button>
                  <Button size="sm" onClick={() => del(s.id)} className="bg-red-600/30 hover:bg-red-600/50 text-red-300">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ Custom Coins Tab ============
const CustomCoinsTab = ({ token }) => {
  const [coins, setCoins] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    symbol: '',
    name: '',
    algorithm: 'randomx',
    coingecko_id: '',
    manual_price_usd: '',
    block_reward: 1,
    block_time_seconds: 60,
    network_hashrate: 1e9,
  });
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/crypto/custom-coins`, { headers });
      setCoins(res.data);
    } catch { toast.error('Failed to load custom coins'); }
    finally { setIsLoading(false); }
  };

  const create = async () => {
    if (!form.symbol || !form.name) { toast.error('Symbol & name required'); return; }
    try {
      const payload = {
        ...form,
        manual_price_usd: form.manual_price_usd === '' ? null : parseFloat(form.manual_price_usd),
        coingecko_id: form.coingecko_id || null,
        block_reward: parseFloat(form.block_reward),
        block_time_seconds: parseFloat(form.block_time_seconds),
        network_hashrate: parseFloat(form.network_hashrate),
      };
      await axios.post(`${API}/crypto/custom-coins`, payload, { headers });
      toast.success('Custom coin added');
      setShowForm(false);
      setForm({ ...form, symbol: '', name: '', coingecko_id: '', manual_price_usd: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Remove this coin?')) return;
    try {
      await axios.delete(`${API}/crypto/custom-coins/${id}`, { headers });
      load();
    } catch { toast.error('Failed'); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="space-y-6" data-testid="custom-coins-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Custom Coins</h2>
        <Button onClick={() => setShowForm(!showForm)} className="bg-purple-600 hover:bg-purple-700" data-testid="add-custom-coin-btn">
          <Plus size={18} className="mr-2" /> Add Coin
        </Button>
      </div>

      <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
        <p className="text-purple-300 text-sm">
          <Sparkles className="inline mr-2" size={16} />
          Add any coin we don't support out-of-the-box. Your custom coins are included in the <strong>Auto-Suggest Best</strong> ranking and the profitability calculator.
          Either provide a <strong>CoinGecko ID</strong> (for live prices) or a <strong>fixed USD price</strong>.
        </p>
      </div>

      {showForm && (
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <Input placeholder="Symbol (e.g., MYCOIN)" value={form.symbol} onChange={e => setForm({...form, symbol: e.target.value.toUpperCase()})} className="bg-gray-900 border-gray-700 text-white" data-testid="custom-symbol-input" />
            <Input placeholder="Full Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-gray-900 border-gray-700 text-white" data-testid="custom-name-input" />
            <Input placeholder="Algorithm (e.g., randomx, sha256)" value={form.algorithm} onChange={e => setForm({...form, algorithm: e.target.value})} className="bg-gray-900 border-gray-700 text-white" />
            <Input placeholder="CoinGecko ID (e.g., bitcoin) — optional" value={form.coingecko_id} onChange={e => setForm({...form, coingecko_id: e.target.value})} className="bg-gray-900 border-gray-700 text-white" />
            <Input type="number" step="0.00000001" placeholder="Manual USD price (if no CoinGecko ID)" value={form.manual_price_usd} onChange={e => setForm({...form, manual_price_usd: e.target.value})} className="bg-gray-900 border-gray-700 text-white" />
            <Input type="number" step="0.01" placeholder="Block Reward" value={form.block_reward} onChange={e => setForm({...form, block_reward: e.target.value})} className="bg-gray-900 border-gray-700 text-white" />
            <Input type="number" placeholder="Block Time (seconds)" value={form.block_time_seconds} onChange={e => setForm({...form, block_time_seconds: e.target.value})} className="bg-gray-900 border-gray-700 text-white" />
            <Input type="number" placeholder="Network Hashrate (H/s)" value={form.network_hashrate} onChange={e => setForm({...form, network_hashrate: e.target.value})} className="bg-gray-900 border-gray-700 text-white" />
          </div>
          <Button onClick={create} className="bg-gradient-to-r from-purple-600 to-cyan-600" data-testid="save-custom-coin-btn">Save Coin</Button>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-purple-500" size={32} /></div>
      ) : coins.length === 0 ? (
        <div className="bg-gray-800 rounded-xl p-12 border border-gray-700 text-center">
          <Sparkles size={48} className="mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400">No custom coins yet.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {coins.map(c => (
            <div key={c.id} className="bg-gray-800 rounded-xl p-4 border border-gray-700" data-testid={`custom-coin-${c.symbol}`}>
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="text-xl font-bold text-white">{c.symbol}</p>
                  <p className="text-xs text-gray-400">{c.name}</p>
                </div>
                <Button size="sm" onClick={() => del(c.id)} className="bg-red-600/30 hover:bg-red-600/50 text-red-300">
                  <Trash2 size={14} />
                </Button>
              </div>
              <div className="text-sm space-y-1 text-gray-300">
                <p><span className="text-gray-500">Algo:</span> {c.algorithm}</p>
                <p><span className="text-gray-500">Price:</span> {c.coingecko_id ? `live (${c.coingecko_id})` : `$${c.manual_price_usd}`}</p>
                <p><span className="text-gray-500">Block reward:</span> {c.block_reward}</p>
                <p><span className="text-gray-500">Block time:</span> {c.block_time_seconds}s</p>
                <p><span className="text-gray-500">Network H/s:</span> {c.network_hashrate.toLocaleString()}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ Notifications Bell (header) ============
const NotificationsBell = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const unread = items.filter(n => !n.read).length;
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/crypto/notifications`, { headers });
      setItems(res.data);
    } catch { /* silent */ }
  };

  const markAll = async () => {
    try {
      await axios.post(`${API}/crypto/notifications/mark-all-read`, {}, { headers });
      load();
    } catch { /* silent */ }
  };

  const del = async (id) => {
    try {
      await axios.delete(`${API}/crypto/notifications/${id}`, { headers });
      load();
    } catch { /* silent */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // poll every 30s
    return () => clearInterval(t);
  }, []); // eslint-disable-line

  return (
    <div className="relative" data-testid="notifications-bell">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
        data-testid="notifications-toggle"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="notifications-badge">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 max-h-[70vh] overflow-y-auto bg-gray-800 rounded-xl border border-gray-700 shadow-2xl z-50" data-testid="notifications-dropdown">
          <div className="flex justify-between items-center p-3 border-b border-gray-700">
            <h3 className="font-bold text-white">Notifications</h3>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button onClick={markAll} className="text-xs text-purple-400 hover:text-purple-300">Mark all read</button>
              )}
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white">
                <X size={16} />
              </button>
            </div>
          </div>
          {items.length === 0 ? (
            <p className="p-6 text-center text-gray-400 text-sm">No notifications yet. Price alerts you create will show up here.</p>
          ) : (
            <div className="divide-y divide-gray-700">
              {items.map(n => (
                <div key={n.id} className={`p-3 hover:bg-gray-700/50 ${!n.read ? 'bg-purple-500/5' : ''}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${!n.read ? 'text-white' : 'text-gray-400'}`}>{n.title}</p>
                      <p className="text-xs text-gray-400 mt-1">{n.message}</p>
                      <p className="text-xs text-gray-600 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                    </div>
                    <button onClick={() => del(n.id)} className="text-gray-500 hover:text-red-400 flex-shrink-0">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============ Getting Started Guide ============
const GuideStep = ({ number, title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden" data-testid={`guide-step-${number}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-4 p-5 text-left hover:bg-gray-700/30 transition-colors"
      >
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-cyan-600 flex items-center justify-center text-white font-bold">
          {number}
        </div>
        <h3 className="flex-1 text-lg font-bold text-white">{title}</h3>
        {open ? <ChevronDown className="text-gray-400" size={20} /> : <ChevronRightIcon className="text-gray-400" size={20} />}
      </button>
      {open && (
        <div className="p-5 pt-0 pl-19 text-gray-300 space-y-3 text-sm leading-relaxed">
          {children}
        </div>
      )}
    </div>
  );
};

const CodeBlock = ({ children }) => (
  <pre className="bg-gray-950 rounded-lg p-3 my-2 overflow-x-auto text-xs text-cyan-300 font-mono border border-gray-800">
    {children}
  </pre>
);

const GuideTab = ({ setActiveTab }) => {
  const dismiss = () => {
    localStorage.setItem('guideSeen', '1');
    toast.success('Welcome to CryptoMiner Dashboard!');
    setActiveTab('dashboard');
  };

  return (
    <div className="space-y-6" data-testid="guide-tab">
      <div className="bg-gradient-to-br from-purple-600/20 via-pink-600/10 to-cyan-600/20 rounded-2xl p-8 border border-purple-500/30">
        <h2 className="text-3xl font-bold text-white mb-2">Welcome — let's get you mining 🚀</h2>
        <p className="text-gray-300">
          This dashboard manages and monitors your mining setup. It does <strong>not</strong> mine on its own —
          it talks to your local mining software (XMRig, CGMiner, etc.) to show you real-time stats.
          Follow these steps to get started.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
          <Wallet className="text-cyan-400 mb-2" size={28} />
          <h4 className="text-white font-bold mb-1">Wallet Address</h4>
          <p className="text-xs text-gray-400">Where the pool sends your earnings. Can be a Binance deposit address or any wallet you own.</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
          <Cpu className="text-purple-400 mb-2" size={28} />
          <h4 className="text-white font-bold mb-1">Mining Software</h4>
          <p className="text-xs text-gray-400">Free open-source apps (XMRig, CGMiner) that run on your PC and do the actual mining work.</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
          <BarChart3 className="text-emerald-400 mb-2" size={28} />
          <h4 className="text-white font-bold mb-1">This Dashboard</h4>
          <p className="text-xs text-gray-400">Reads stats from your miner's local API, ranks coins by profit, and tracks earnings.</p>
        </div>
      </div>

      <GuideStep number={1} title="Pick the most profitable coin for your hardware" defaultOpen={true}>
        <p>
          Click <button className="text-purple-400 underline hover:text-purple-300" onClick={() => setActiveTab('miners')}>Miners → Auto-Suggest Best</button>,
          enter your <strong>hashrate</strong> (how fast your hardware mines — usually shown in MH/s or H/s on benchmark sites),
          your <strong>power consumption</strong> in watts, and your electricity cost ($/kWh).
        </p>
        <p>The dashboard ranks all built-in + custom coins by live daily profit using real network hashrate data.</p>
        <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
          <p className="text-blue-300 text-xs">
            <strong>Don't know your hashrate?</strong> Search for your CPU/GPU model on{' '}
            <a href="https://2cryptocalc.com/" target="_blank" rel="noopener noreferrer" className="underline">2CryptoCalc</a>{' '}
            or <a href="https://whattomine.com/" target="_blank" rel="noopener noreferrer" className="underline">WhatToMine</a>.
          </p>
        </div>
      </GuideStep>

      <GuideStep number={2} title="Get a wallet address to receive payouts">
        <p>The pool sends your mining earnings to a wallet address you control. You have two options:</p>
        <div className="space-y-2 mt-2">
          <div className="p-3 bg-gray-900 rounded-lg border border-emerald-500/30">
            <p className="font-bold text-emerald-400">Option A — Binance deposit address (recommended for beginners)</p>
            <ol className="list-decimal list-inside text-xs text-gray-400 mt-1 space-y-1">
              <li>Log in to <a href="https://www.binance.com/" target="_blank" rel="noopener noreferrer" className="underline text-cyan-400">Binance</a></li>
              <li>Go to <strong>Wallet → Spot Wallet → Deposit</strong></li>
              <li>Pick the coin you'll mine (e.g., XMR, ETC, RVN)</li>
              <li>Pick the network (most pools want the coin's native chain)</li>
              <li>Copy the deposit address — that's what you paste into the dashboard</li>
            </ol>
            <p className="text-xs text-gray-500 mt-2">Earnings can then be swapped/sold to USD on Binance.</p>
          </div>
          <div className="p-3 bg-gray-900 rounded-lg border border-purple-500/30">
            <p className="font-bold text-purple-400">Option B — Self-custody wallet</p>
            <p className="text-xs text-gray-400 mt-1">
              Use a wallet you control: Trust Wallet, MetaMask (for ETC/RVN/etc.), Monero GUI Wallet (XMR), Ledger/Trezor hardware wallet, etc.
              Get the receive address from your wallet app and paste it in.
            </p>
          </div>
        </div>
        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <p className="text-amber-300 text-xs">
            <strong>Note:</strong> The "Wallet Address" field is <em>not</em> connected to the Binance API tab.
            They're separate features. The wallet field just receives payouts from the pool.
          </p>
        </div>
      </GuideStep>

      <GuideStep number={3} title="Install mining software on your rig (XMRig / CGMiner)">
        <p>
          The dashboard <strong>does not run mining itself</strong> — it just monitors. You need to install free,
          open-source mining software on the computer that will mine.
        </p>
        <div className="space-y-2 mt-2">
          <div className="p-3 bg-gray-900 rounded-lg">
            <p className="font-bold text-white">XMRig (CPU mining — RandomX coins like Monero)</p>
            <p className="text-xs text-gray-400 mt-1">Download from <a href="https://github.com/xmrig/xmrig/releases" target="_blank" rel="noopener noreferrer" className="underline text-cyan-400">github.com/xmrig/xmrig</a></p>
            <p className="text-xs text-gray-400 mt-1">Run with HTTP API enabled so this dashboard can read stats:</p>
            <CodeBlock>{`./xmrig \\
  -o stratum+tcp://monero.herominers.com:1111 \\
  -u <YOUR_WALLET_ADDRESS>.worker1 \\
  -p x \\
  --http-host 127.0.0.1 \\
  --http-port 4444 \\
  --http-access-token <ANY_RANDOM_STRING>`}</CodeBlock>
          </div>
          <div className="p-3 bg-gray-900 rounded-lg">
            <p className="font-bold text-white">CGMiner (ASIC / GPU — SHA-256, Scrypt, etc.)</p>
            <p className="text-xs text-gray-400 mt-1">Download from <a href="https://github.com/ckolivas/cgminer" target="_blank" rel="noopener noreferrer" className="underline text-cyan-400">github.com/ckolivas/cgminer</a></p>
            <CodeBlock>{`cgminer \\
  -o stratum+tcp://btc.f2pool.com:3333 \\
  -u <YOUR_WALLET_ADDRESS>.worker1 \\
  -p x \\
  --api-listen \\
  --api-port 4028`}</CodeBlock>
          </div>
        </div>
      </GuideStep>

      <GuideStep number={4} title="Add the miner to this dashboard">
        <p>
          Once your mining software is running, click{' '}
          <button className="text-purple-400 underline hover:text-purple-300" onClick={() => setActiveTab('miners')}>Miners → Add Miner</button>{' '}
          (manual) or use <strong>Auto-Suggest Best → Auto-Provision</strong> for one-click setup.
        </p>
        <ul className="list-disc list-inside text-xs space-y-1">
          <li><strong>Name:</strong> any label (e.g., "Office Rig")</li>
          <li><strong>API Host:</strong> usually <code className="text-cyan-300">127.0.0.1</code> if mining on the same machine as this server. Use the rig's LAN IP if remote.</li>
          <li><strong>API Port:</strong> <code className="text-cyan-300">4444</code> for XMRig, <code className="text-cyan-300">4028</code> for CGMiner (must match your miner's <code>--http-port</code>).</li>
          <li><strong>API Token:</strong> if you set <code>--http-access-token</code>, paste it here.</li>
        </ul>
      </GuideStep>

      <GuideStep number={5} title="🔥 SHORTCUT: Let the dashboard run the miner for you (self-hosted only)">
        <p>
          If you self-host this app on your <strong>own mining rig</strong> via Docker Compose,
          you can skip steps 3–4 entirely. The dashboard can:
        </p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>Auto-download XMRig from official GitHub releases (~50 MB)</li>
          <li>Pick the most profitable RandomX coin live</li>
          <li>Launch XMRig as a managed subprocess</li>
          <li>Start/stop/restart it from the Miners tab</li>
        </ul>
        <p className="mt-2"><strong>To enable</strong>, in your <code className="text-cyan-300">.env</code> set:</p>
        <CodeBlock>{`MINER_LAUNCH_ENABLED=true`}</CodeBlock>
        <p>
          Then restart with <code className="text-cyan-300">docker compose up -d</code>.
          A green <strong>"Start Mining (Auto)"</strong> button will appear at the top of the Miners tab.
        </p>
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-red-300 text-xs">
            <strong>Warning:</strong> Don't enable this on shared cloud hosting (AWS, DigitalOcean, Emergent cloud preview, etc.).
            Most providers ban crypto mining in their ToS, and your account would be suspended. This feature is meant for hardware
            you physically own and pay electricity for.
          </p>
        </div>
      </GuideStep>

      <GuideStep number={6} title='Why am I getting "Unable to connect to miner API"?'>
        <p>This error means the dashboard <strong>can&apos;t reach a running miner</strong> at the API host:port you configured. Most common reasons:</p>
        <ol className="list-decimal list-inside space-y-2 mt-2">
          <li><strong>The miner isn&apos;t running.</strong> Start XMRig/CGMiner first (see Step 3), then click "Check Stats" again.</li>
          <li><strong>The API isn&apos;t enabled.</strong> XMRig needs <code className="text-cyan-300">--http-host 127.0.0.1 --http-port 4444</code>. CGMiner needs <code className="text-cyan-300">--api-listen</code>.</li>
          <li><strong>Wrong host/port.</strong> Double-check the miner&apos;s console output — it prints "HTTP API binding to 127.0.0.1:4444" when ready.</li>
          <li><strong>Firewall.</strong> If the miner is on a different machine, allow inbound TCP on the API port.</li>
          <li><strong>Token mismatch.</strong> If your miner uses <code>--http-access-token</code>, the same token must be saved in the dashboard's miner config.</li>
        </ol>
        <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg mt-3">
          <p className="text-blue-300 text-xs">
            <strong>Quick test:</strong> from the rig running the miner, open a terminal and try:
          </p>
          <CodeBlock>curl http://127.0.0.1:4444/2/summary</CodeBlock>
          <p className="text-blue-300 text-xs">If you get JSON back — the miner API is fine. If "connection refused" — the miner isn&apos;t running or the API isn&apos;t enabled.</p>
        </div>
      </GuideStep>

      <GuideStep number={7} title="(Optional) Connect Binance for deposit monitoring">
        <p>
          If you mine to a Binance deposit address, you can connect a <strong>read-only</strong> Binance API key
          to see your balances and deposit history right inside this dashboard.
        </p>
        <ol className="list-decimal list-inside space-y-1 mt-2">
          <li>On Binance, go to <strong>Account → API Management → Create API</strong></li>
          <li>Enable <strong>Read-Only</strong> permissions only. Disable trading and withdrawals.</li>
          <li>Whitelist your IP if possible (extra security).</li>
          <li>Paste the keys into <button className="text-purple-400 underline hover:text-purple-300" onClick={() => setActiveTab('binance')}>Binance tab</button> in this dashboard.</li>
        </ol>
        <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
          <p className="text-emerald-300 text-xs">
            <Shield className="inline mr-1" size={14} />
            <strong>Security:</strong> Your keys are stored only in this browser session and never saved on our server.
            They&apos;re cleared when you close the tab.
          </p>
        </div>
      </GuideStep>

      <GuideStep number={8} title="Set up price alerts & explore opportunities">
        <ul className="list-disc list-inside space-y-1">
          <li><button className="text-purple-400 underline hover:text-purple-300" onClick={() => setActiveTab('alerts')}>Price Alerts</button> — get notified when a coin hits your target price (auto-checked every 60s, persists in your notifications).</li>
          <li><button className="text-purple-400 underline hover:text-purple-300" onClick={() => setActiveTab('arbitrage')}>Arbitrage</button> — see live spreads between Binance and KuCoin.</li>
          <li><button className="text-purple-400 underline hover:text-purple-300" onClick={() => setActiveTab('opportunities')}>Opportunities</button> — curated airdrop list (read-only info, no claim execution).</li>
          <li><button className="text-purple-400 underline hover:text-purple-300" onClick={() => setActiveTab('wallet')}>Wallet Scan</button> — analyze any Ethereum address for forgotten dust tokens.</li>
        </ul>
      </GuideStep>

      <div className="flex justify-between items-center pt-2">
        <p className="text-sm text-gray-400">You can revisit this guide anytime from the sidebar.</p>
        <Button onClick={dismiss} className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700" data-testid="guide-dismiss-btn">
          <CheckCircle size={16} className="mr-2" />
          Got it — take me to the dashboard
        </Button>
      </div>
    </div>
  );
};

export default CryptoDashboard;
